import React, { useState, useEffect, useContext } from 'react';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Calendar, Users, LogOut, Plus, Trash2 } from 'lucide-react';

export default function UltraAdminDashboard() {
  const { user, logout } = useContext(AuthContext);
  const [exhibitions, setExhibitions] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [open, setOpen] = useState(false);
  const [newExhibition, setNewExhibition] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    venue: '',
    organizer_id: ''
  });

  useEffect(() => {
    fetchExhibitions();
    fetchOrganizers();
  }, []);

  const fetchExhibitions = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/exhibitions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExhibitions(res.data);
    } catch (error) {
      toast.error('Fuarlar yüklenemedi');
    }
  };

  const fetchOrganizers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/users?role=organizer`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrganizers(res.data);
    } catch (error) {
      toast.error('Organizatörler yüklenemedi');
    }
  };

  const handleCreateExhibition = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/exhibitions`, newExhibition, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fuar oluşturuldu!');
      setOpen(false);
      setNewExhibition({ name: '', description: '', start_date: '', end_date: '', venue: '', organizer_id: '' });
      fetchExhibitions();
    } catch (error) {
      toast.error('Fuar oluşturulamadı');
    }
  };

  const handleDeleteExhibition = async (id) => {
    if (!window.confirm('Bu fuarı silmek istediğinize emin misiniz?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/exhibitions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Fuar silindi');
      fetchExhibitions();
    } catch (error) {
      toast.error('Fuar silinemedi');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="dashboard-title">Ultra Admin Dashboard</h1>
            <p className="text-sm text-gray-500">Hoşgeldiniz, {user?.name}</p>
          </div>
          <Button onClick={logout} variant="outline" data-testid="logout-btn">
            <LogOut className="w-4 h-4 mr-2" />
            Çıkış
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-3xl font-bold text-gray-900">Fuarlar</h2>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-exhibition-btn">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Fuar Oluştur
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Yeni Fuar Oluştur</DialogTitle>
                <DialogDescription>Fuar bilgilerini doldurun</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateExhibition} className="space-y-4" data-testid="exhibition-form">
                <div className="space-y-2">
                  <Label htmlFor="name">Fuar Adı</Label>
                  <Input
                    id="name"
                    value={newExhibition.name}
                    onChange={(e) => setNewExhibition({...newExhibition, name: e.target.value})}
                    required
                    data-testid="exhibition-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Açıklama</Label>
                  <Textarea
                    id="description"
                    value={newExhibition.description}
                    onChange={(e) => setNewExhibition({...newExhibition, description: e.target.value})}
                    data-testid="exhibition-description-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_date">Başlangıç Tarihi</Label>
                    <Input
                      id="start_date"
                      type="date"
                      value={newExhibition.start_date}
                      onChange={(e) => setNewExhibition({...newExhibition, start_date: e.target.value})}
                      required
                      data-testid="exhibition-start-date-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="end_date">Bitiş Tarihi</Label>
                    <Input
                      id="end_date"
                      type="date"
                      value={newExhibition.end_date}
                      onChange={(e) => setNewExhibition({...newExhibition, end_date: e.target.value})}
                      required
                      data-testid="exhibition-end-date-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="venue">Mekan</Label>
                  <Input
                    id="venue"
                    value={newExhibition.venue}
                    onChange={(e) => setNewExhibition({...newExhibition, venue: e.target.value})}
                    required
                    data-testid="exhibition-venue-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="organizer">Organizatör</Label>
                  <Select
                    value={newExhibition.organizer_id}
                    onValueChange={(value) => setNewExhibition({...newExhibition, organizer_id: value})}
                    required
                  >
                    <SelectTrigger data-testid="exhibition-organizer-select">
                      <SelectValue placeholder="Organizatör seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizers.length === 0 ? (
                        <div className="p-2 text-sm text-gray-500">Henüz organizatör kayıtlı değil</div>
                      ) : (
                        organizers.map(org => (
                          <SelectItem key={org.id} value={org.id} data-testid={`organizer-${org.id}`}>
                            {org.name} ({org.email})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">
                    {organizers.length} organizatör mevcut
                  </p>
                </div>
                <Button type="submit" className="w-full" data-testid="submit-exhibition-btn">
                  Oluştur
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {exhibitions.map(exhibition => (
            <Card key={exhibition.id} className="hover:shadow-lg transition-shadow" data-testid={`exhibition-${exhibition.id}`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  {exhibition.name}
                </CardTitle>
                <CardDescription>{exhibition.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <p><strong>Mekan:</strong> {exhibition.venue}</p>
                  <p><strong>Tarih:</strong> {exhibition.start_date} - {exhibition.end_date}</p>
                </div>
                <Button
                  onClick={() => handleDeleteExhibition(exhibition.id)}
                  variant="destructive"
                  size="sm"
                  className="mt-4 w-full"
                  data-testid={`delete-exhibition-${exhibition.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Sil
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}